import className from 'classnames';
import { ReactSVG } from 'react-svg';
import moment from 'utils/dayjs';

import { useTranslation } from 'utils/i18n';
import { AVAILABILITY_STATUSES } from '../../constants/fieldData';
import TimeSlot from '../timeSlot';

interface Slot {
  startTime: string;
  endTime: string;
  available?: string;
  combined?: boolean;
}

const SpaceTimeSlots = ({
  space,
  slots = [],
  onRemove,
  hasError = false,
  hasRecurrence = false,
  availability = {},
}: {
  // The API serialises priceIncVat as a decimal string; it is coerced below.
  space: { id: number; name: string; priceIncVat: number | string };
  slots?: Slot[];
  onRemove: () => void;
  hasError?: boolean;
  hasRecurrence?: boolean;
  availability?: Record<string, { spaceId: number }[]>;
}) => {
  const { t } = useTranslation('common');
  const classes = className({
    space: true,
    'space--error': hasError,
  });

  const costForSlot = (slot: Slot, occurrences: number) => {
    const hours =
      moment(slot.endTime, 'HH:mm').diff(
        moment(slot.startTime, 'HH:mm'),
        'minute',
        true
      ) / 60.0;

    return hours * Number(space.priceIncVat) * occurrences;
  };

  const renderSelectedSlots = () => {
    return slots.length ? (
      slots.map((slot: Slot) => {
        const slotKey = `${slot.startTime}-${slot.endTime}`;
        const occurrences = availability[slotKey]
          ? availability[slotKey].filter(entry => entry.spaceId === space.id)
              .length
          : 1;

        return (
          <ul className="space__slots" key={slotKey}>
            <TimeSlot
              startTime={slot.startTime}
              endTime={slot.endTime}
              cost={costForSlot(slot, occurrences)}
              hasHelp={!!slot.combined}
              hasError={slot.available !== AVAILABILITY_STATUSES.AVAILABLE.flag}
              errorLabel={
                AVAILABILITY_STATUSES[
                  slot.available as keyof typeof AVAILABILITY_STATUSES
                ].label
              }
            />
          </ul>
        );
      })
    ) : (
      <ul className="space__slots">
        <TimeSlot />
      </ul>
    );
  };

  return (
    <li className={classes} data-testid="spaceItem" data-cy="spaceItem">
      <div className="space__item space__details">
        <span className="space__name">
          {hasError && (
            <i
              className="fas fa-exclamation-triangle space__icon"
              data-testid="spaceListItemErrorIcon"
            />
          )}
          {space.name}
          {hasRecurrence && (
            <ReactSVG
              src="/images/icons/basket/recurring.svg"
              width={17}
              height={17}
            />
          )}
        </span>

        <button
          className="button button--small"
          onClick={onRemove}
          data-testid="quickBookRemoveSpace"
        >
          {t('quick_book.space.remove')}
        </button>
      </div>

      {renderSelectedSlots()}
    </li>
  );
};

export default SpaceTimeSlots;
